// API client with JWT auth, auto-refresh, and typed helpers.
// Token stored in memory only — never localStorage.

type GetTokenFn = () => Promise<string | null>;
type OnUnauthorizedFn = () => void;

let getToken: GetTokenFn = async () => null;
let onUnauthorized: OnUnauthorizedFn = () => {};

/** Called once by AuthProvider on mount to wire up token access. */
export function configureApiClient(
  tokenFn: GetTokenFn,
  unauthorizedFn: OnUnauthorizedFn
) {
  getToken = tokenFn;
  onUnauthorized = unauthorizedFn;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function request<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeaders()),
    ...(options.headers as Record<string, string> || {}),
  };

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, { ...options, headers, credentials: 'include' });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body.error) message = body.error;
      else if (body.message) message = body.message;
      else if (body.errors) {
        // ASP.NET validation errors
        const msgs = Object.values(body.errors).flat();
        message = msgs.join(' ');
      }
    } catch {
      // response wasn't JSON
    }

    // 401 on an auth endpoint (login, 2fa) means bad credentials — show the real error.
    // 401 elsewhere means the session expired — log out and redirect.
    if (response.status === 401 && !url.startsWith('/api/auth/')) {
      onUnauthorized();
      throw new ApiError('Session expired. Please sign in again.', 401);
    }

    throw new ApiError(message, response.status);
  }

  // 204 No Content or empty body
  const text = await response.text();
  if (!text) return undefined as unknown as T;
  return JSON.parse(text) as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

export function apiGet<T>(url: string): Promise<T> {
  return request<T>(url);
}

export function apiPost<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPut<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: 'PUT',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: 'PATCH',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T>(url: string): Promise<T> {
  return request<T>(url, { method: 'DELETE' });
}

export function apiUpload<T>(url: string, formData: FormData): Promise<T> {
  return request<T>(url, {
    method: 'POST',
    body: formData,
  });
}

/** Fetch a URL and return the raw response text — use when the response is not JSON (e.g. HTML). */
export async function apiGetText(url: string): Promise<string> {
  const headers = await authHeaders();
  const response = await fetch(url, { headers, credentials: 'include' });
  if (response.status === 401 && !url.startsWith('/api/auth/')) {
    onUnauthorized();
    throw new ApiError('Session expired. Please sign in again.', 401);
  }
  if (!response.ok) throw new ApiError(`Request failed (${response.status})`, response.status);
  return response.text();
}

/** Download a file as a Blob — doesn't parse JSON. */
export async function apiDownload(url: string): Promise<{ blob: Blob; fileName: string }> {
  const headers = await authHeaders();
  const response = await fetch(url, { headers, credentials: 'include' });

  if (response.status === 401) {
    onUnauthorized();
    throw new ApiError('Session expired. Please sign in again.', 401);
  }
  if (!response.ok) {
    throw new ApiError(`Download failed (${response.status})`, response.status);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?(.+?)"?$/);
  const fileName = match ? match[1] : 'download';

  return { blob, fileName };
}
